import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';
import { MUTATION_REGISTER_SNAPSHOT_LISTENER, MUTATION_UNREGISTER_SNAPSHOT_LISTENER } from '@/shared/store/constants';
import { getAccessibleAnalysisIdForDocument } from '@/shared/store/analyses';
import { ReflectionAccessValidator } from '@/shared/utils/Reflection';
import { now } from '@/tools/momentUtils';
import {
  onSnapshotById,
  getNewDocumentId,
  getById,
  find,
  findRawGroup,
  onSnapshotByQueryOnSubCollection,
  onSnapshotByQuery,
  addElementToArray,
  removeElementToArray,
  update,
  setMerge,
} from '@/firebase/firestoreFunctions';
import { getFieldValue, configureQueryByCollectionPath, executeQueryRaw } from '@/shared/firebase/firestore';
import { getCurrentUserUid } from '@/firebase/authFunctions';
import { FirestoreQuery } from '@/firebase/firestoreQuery';
import { delay } from '@/shared/tools/promiseUtils';
import { buildAclObject } from '@/shared/store/aclHelper';
import { types as authentication } from '@/shared/store/authentication';
import {
  getAnalysesFromIds,
} from '@/shared/store/analysisHelper';
import {
  createDefaultReflectionCategories,
  createDefaultReflectionDocumentRetentions,
  editReflection,
  pingReflection,
  removeReflectionDocument,
  saveNewReflection,
  setReflectionDocument,
  setReflectionReport,
  updateReflectionDocument,
  updateReflectionReport,
} from './reflectionsHelper';
import bugsnagClient from '../bugsnag';
import { types as ux } from './ux';

const MODULE_PREFIX = 'reflections';
const GETTER_CREATE_IN_PROGRESS = 'createInProgress';
const GETTER_FETCH_IN_PROGRESS = 'fetchInProgress';
const GETTER_ADD_ANALYSIS_TO_REFLECTION_IN_PROGRESS = 'addAnalysisToReflectionInProgress';
const GETTER_REFLECTION = 'reflection';
const GETTER_REFLECTION_ID = 'reflectionId';
const GETTER_REFLECTION_READONLY = 'reflectionReadOnly';
const GETTER_CURRENT_REFLECTION_COMMENTS = 'comments';
const GETTER_CUR_REFLECTION_COMMENTS = 'curReflectionComments';
const GETTER_CURRENT_REPORT = 'report';
const GETTER_CURRENT_ANALYSES = 'currentReflectionAnalyses';
const GETTER_CURRENT_REPORTS = 'reports';
const GETTER_REFLECTIONS = 'reflections';
const GETTER_LOADING_COMMENTS = 'loadingComments';
const GETTER_FETCH_CURRENT_REFLECTION_DATA = 'fetchCurrentReflectionData';
const GETTER_FETCH_PREVIOUS_REFLECTION_DATA = 'fetchPreviousReflectionData';
const MUTATION_SET_CURRENT_REFLECTION_ID = 'SET_CURRENT_REFLECTION_ID';
const MUTATION_SET_CURRENT_REPORT_ID = 'SET_CURRENT_REPORT_ID';
const MUTATION_SET_FETCH_IN_PROGRESS = 'SET_FETCH_IN_PROGRESS';
const MUTATION_SET_FETCH_TIMEOUT_ID = 'SET_FETCH_TIMEOUT_ID';
const MUTATION_SET_CREATE_IN_PROGRESS = 'SET_CREATE_IN_PROGRESS';
const MUTATION_SET_PERMISSION = 'SET_PERMISSION';
const MUTATION_FETCHED_ANALYSES = 'FETCHED_ANALYSES';
const MUTATION_FETCHED_CURRENT_REPORT = 'FETCHED_CURRENT_REPORT';
const MUTATION_FETCHED_REFLECTION = 'FETCHED_REFLECTION';
const MUTATION_FETCHED_REFLECTION_COMMENTS = 'FETCHED_REFLECTION_COMMENTS';
const MUTATION_FETCHED_CUR_REFLECTION_COMMENTS = 'FETCHED_CUR_REFLECTION_COMMENTS';
const MUTATION_FETCHED_REFLECTIONS = 'FETCHED_REFLECTIONS';
const MUTATION_ARCHIVE_REFLECTIONS = 'MUTATION_ARCHIVE_REFLECTIONS';
const MUTATION_FETCHED_REPORTS = 'FETCHED_REPORTS';
const MUTATION_SET_ADD_ANALYSIS_TO_REFLECTION_IN_PROGRESS = 'SET_ADD_ANALYSIS_TO_REFLECTION_IN_PROGRESS';
const MUTATION_SET_LOADING_COMMENTS = 'SET_LOADING_COMMENTS';
const MUTATION_SET_CURRENT_REFLECTION_DATA = 'SET_CURRENT_REFLECTION_DATA';
const MUTATION_SET_PREVIOUS_REFLECTION_DATA = 'SET_PREVIOUS_REFLECTION_DATA';
const ACTION_CREATE_REFLECTION = 'createReflection';
const ACTION_CREATE_CUR_REFLECTION_COMMENT = 'createCurReflectionComment';
const ACTION_FETCH_REFLECTION = 'fetchReflection';
const ACTION_MARK_TEMPLATE_APPLIED = 'markTemplateApplied';
const ACTION_GET_REFLECTION_COMMENT_THREAD = 'getReflectionCommentThread';
const ACTION_GET_CURRENT_REPORT_BY_ID = 'getCurrentReportById';
const ACTION_SET_ANALYSIS_REFLECTION_RETENTION = 'setAnalysisReflectionRetention';
const ACTION_UPDATE_CURRENT_REFLECTION_DATA_STATE = 'updateCurrentReflectionDataState';
const ACTION_UPDATE_CURRENT_REFLECTION_DATA = 'updateCurrentReflectionData';
const ACTION_SUBSCRIBE_TO_REFLECTION_CHANGES = 'subscribeToReflectionChanges';
const ACTION_UPDATE_REFLECTION = 'updateReflection';
const ACTION_UPDATE_FETCHED_REFLECTIONS = 'updateFetchedReflections';
const ACTION_ARCHIVE_REFLECTION = 'archiveReflection';
const ACTION_HEADER_HAS_JOURNAL_SECTION = 'headerHasJournalSection';
const ACTION_VALIDATE_SUPPORTING_DOCS_ACL = 'validateSupportingDocsACL';

// ///////////////////////////////
// TODO Major cleanup to be done here now that we have tiptap with an external
// Yjs server and database to handle storing the content and handle
// all the collaborative logic
// ///////////////////////////////

const SNAPSHOT_DEBOUNCE = 1000;
const CATEGORIES_ID_SEPARATOR = '.categories.';
const SNAPSHOT_IDS = {
  COMMENTS: 'currentReflectionReportComments',
  DOCUMENTS: 'currentReflectionDocuments',
  REFLECTION: 'currentReflection',
  REFLECTION_PERMISSION: 'currentReflectionPermission',
  REFLECTIONS: 'reflections',
  REPORTS: 'currentReflectionReports',
  REFLECTION_ONCHANGE: 'currentReflectionOnChange',
};

const initialState = {
  addAnalysisToReflectionInProgress: {},
  comments: [],
  curReflectionComments: [],
  createInProgress: false,
  currentReflectionId: undefined,
  currentReflectionAnalyses: undefined,
  currentReflectionData: {},
  previousReflectionData: {},
  currentReportId: undefined,
  fetchInProgress: false,
  fetchTimeoutId: null,
  loadingComments: false,
  reflection: {},
  reflectionCategoryDraft: undefined,
  reflections: [],
  reports: undefined,
  report: undefined,
  permission: '',
};

const getters = {
  [GETTER_CREATE_IN_PROGRESS]: state => state.createInProgress,
  [GETTER_FETCH_IN_PROGRESS]: state => state.fetchInProgress,
  [GETTER_ADD_ANALYSIS_TO_REFLECTION_IN_PROGRESS]: (state, { reflectionId, analysisId }) => {
    if (state.addAnalysisToReflectionInProgress) {
      return state.addAnalysisToReflectionInProgress[reflectionId]
        && state.addAnalysisToReflectionInProgress[reflectionId][analysisId];
    }

    return false;
  },
  [GETTER_CURRENT_REFLECTION_COMMENTS]: state => state.comments,
  [GETTER_CUR_REFLECTION_COMMENTS]: state => state.curReflectionComments,
  [GETTER_CURRENT_REPORT]: state => state.report,
  [GETTER_CURRENT_ANALYSES]: state => state.currentReflectionAnalyses || [],
  [GETTER_CURRENT_REPORTS]: state => state.reports,
  [GETTER_LOADING_COMMENTS]: state => state.loadingComments,
  [GETTER_REFLECTION]: state => state.reflection,
  [GETTER_REFLECTION_ID]: state => (state.reflection && state.reflection.id) || undefined,
  reflectionCategoryDraft: state => state.reflectionCategoryDraft,
  [GETTER_REFLECTION_READONLY]: state => state.permission !== 'Owner',
  [GETTER_REFLECTIONS]: state => state.reflections,
  [GETTER_FETCH_CURRENT_REFLECTION_DATA]: state => state.currentReflectionData,
  [GETTER_FETCH_PREVIOUS_REFLECTION_DATA]: state => state.previousReflectionData,
};

const mutations = {
  [MUTATION_SET_CURRENT_REFLECTION_ID]: (state, id) => {
    state.currentReflectionId = id;
  },
  [MUTATION_SET_CURRENT_REPORT_ID]: (state, id) => {
    state.currentReportId = id;
  },
  [MUTATION_FETCHED_ANALYSES]: (state, analyses) => {
    state.currentReflectionAnalyses = analyses;
  },
  [MUTATION_SET_CREATE_IN_PROGRESS]: (state, inProgress) => {
    state.createInProgress = inProgress;
  },
  [MUTATION_SET_PERMISSION]: (state, permission) => {
    state.permission = permission;
  },
  [MUTATION_SET_FETCH_IN_PROGRESS]: (state, inProgress) => {
    state.fetchInProgress = inProgress;
  },
  [MUTATION_SET_FETCH_TIMEOUT_ID]: (state, fetchTimeoutId) => {
    state.fetchTimeoutId = fetchTimeoutId;
  },
  [MUTATION_FETCHED_REFLECTION]: (state, reflection) => {
    state.reflection = cloneDeep(reflection);
  },
  [MUTATION_FETCHED_REFLECTION_COMMENTS]: (state, comments) => {
    state.comments = comments ? [...comments] : [];
  },
  [MUTATION_FETCHED_CUR_REFLECTION_COMMENTS]: (state, comments) => {
    state.curReflectionComments = comments ? [...comments] : [];
  },
  mutateReflectionCategoryDraft: (state, draft) => {
    state.reflectionCategoryDraft = cloneDeep(draft);
  },
  [MUTATION_SET_LOADING_COMMENTS]: (state, loadingComments) => {
    state.loadingComments = loadingComments;
  },
  [MUTATION_FETCHED_CURRENT_REPORT]: (state, report) => {
    state.report = { ...report };
  },
  [MUTATION_FETCHED_REPORTS]: (state, reports) => {
    const { currentReportId } = state;
    let nextReport;
    if (currentReportId) {
      nextReport = reports.filter(({ id }) => (id)).find((report) => report.id === currentReportId);
    } else {
      nextReport = reports.filter(({ id }) => (id)).find((report) => !report.lockedOn);
    }

    state.reports = cloneDeep(reports);

    if (nextReport) {
      state.report = cloneDeep(nextReport);
    }
  },
  [MUTATION_ARCHIVE_REFLECTIONS]: (state, { isArchived, reflectionId }) => {
    const reflections = cloneDeep(state.reflections);
    const reflection = reflections.find(({ id }) => id === reflectionId);

    if (reflection) {
      reflection.isArchived = isArchived;
    }

    Vue.set(state, 'reflections', reflections);
  },
  [MUTATION_FETCHED_REFLECTIONS]: (state, reflections) => {
    const { currentReflectionId } = state;
    const nextReflection = reflections.filter(({ id }) => (id)).find((reflection) => reflection.id === currentReflectionId);
    state.reflections = cloneDeep(reflections);

    if (nextReflection) {
      state.reflection = cloneDeep(nextReflection);
      state.fetchInProgress = false;
    }
  },
  [MUTATION_SET_ADD_ANALYSIS_TO_REFLECTION_IN_PROGRESS]: (state, { analysisId, reflectionId, inProgress }) => {
    if (!state.addAnalysisToReflectionInProgress[reflectionId]) {
      state.addAnalysisToReflectionInProgress[reflectionId] = {};
    }
    state.addAnalysisToReflectionInProgress[reflectionId][analysisId] = inProgress;
  },
  [MUTATION_SET_CURRENT_REFLECTION_DATA]: (state, { data }) => {
    state.previousReflectionData = state.currentReflectionData;
    state.currentReflectionData = data;
  },
  [MUTATION_SET_PREVIOUS_REFLECTION_DATA]: (state, { data }) => {
    state.previousReflectionData = data;
  },
};

async function onReflectionSnapshot({ commit }, reflectionSnapshot) {
  const reflectionSnapshotData = reflectionSnapshot.data();
  if (reflectionSnapshotData) {
    commit(MUTATION_FETCHED_REFLECTION, reflectionSnapshotData);
  }

  commit(MUTATION_SET_FETCH_IN_PROGRESS, false);
}

async function onReflectionAclDenormSnapshot({ commit }, reflectionAclDenormSnapshot) {
  const snapshotData = reflectionAclDenormSnapshot.data();
  if (snapshotData) {
    const { permission } = snapshotData;
    commit(MUTATION_SET_PERMISSION, permission);
  }
}

async function fetchUsersForGroup(groupId) {
  return find('groupUsers', [
    FirestoreQuery.equals('groupId', groupId),
  ]);
}

async function appendActiveUserIds(reflection) {
  const { acl } = reflection;
  const groupIds = acl.filter(entity => entity.entityType === 'Group').map(entity => entity.entityId);
  let userIds = acl.filter(entity => entity.entityType === 'User').map(entity => entity.entityId);
  const users = await Promise.all(groupIds.map(groupId => fetchUsersForGroup(groupId)));
  userIds = uniq(userIds.concat(users.flat().map(user => user.id)));
  return { ...reflection, activeUsers: userIds };
}

async function onReportSnapshots({ commit }, snapshots) {
  if (snapshots && snapshots.docs) {
    const reports = snapshots.docs.map(snapshot => snapshot.data());
    commit(MUTATION_FETCHED_REPORTS, reports);
  }
}

async function onAnalysisSnapshots({ commit }, snapshots) {
  if (snapshots && snapshots.docs) {
    const analyses = snapshots.docs.map(snapshot => snapshot.data());
    const analysisIds = analyses.map(analysis => analysis.id);
    const analysesData = await getAnalysesFromIds('', '', analysisIds);

    analyses.forEach(analysis => {
      const analysisData = analysesData.find(data => data.id === analysis.id);
      analysis.analysisData = analysisData;
    });

    commit(MUTATION_FETCHED_ANALYSES, analyses);
  }
}

async function onReflectionAclSnapshots({ commit }, snapshots) {
  let reflections = [];
  if (snapshots.docs && snapshots.docs.length > 0) {
    reflections = await Promise.all(snapshots.docs.map(acl => getById('reflections', acl.data().reflectionId)));
    // TODO move in a prepare method
    reflections = await Promise.all(reflections.map(reflection => appendActiveUserIds(reflection)));
    reflections = reflections.sort(
      (first, second) => second.createdOn - first.createdOn,
    );
  }
  commit(MUTATION_FETCHED_REFLECTIONS, reflections);
}

const actions = {
  headerHasJournalSection: async (_, { headerId }) => {
    const query = configureQueryByCollectionPath('pipelineJournals', [{
      key: 'headerId',
      operator: '==',
      value: headerId,
    }]);
    const result = await executeQueryRaw(query);

    return !result.empty;
  },
  addReflectionAnalysis: async ({ rootGetters }, { reflectionId, document }) => {
    const userId = rootGetters[authentication.getters.firebaseUid];
    const userDisplayName = rootGetters[authentication.getters.displayName];
    const analysisId = await getAccessibleAnalysisIdForDocument(
      document,
      userId,
      userDisplayName,
      'User',
      userId,
      userDisplayName,
    );
    try {
      await addElementToArray('reflections', reflectionId, 'reflectionDocumentIds', analysisId);
      await setReflectionDocument(analysisId, reflectionId, {
        id: analysisId,
        reflectionId,
        ...createDefaultReflectionDocumentRetentions(EReflectionAnalysisRetention.considered),
      });
    } catch (e) {
      console.error('FAILURE: reflections.actions.addReflectionAnalysis', e);
      bugsnagClient.notify(e);
    }
  },
  addReflectionCategoryAnalysis: async ({ rootGetters }, { reflectionId, document, ids }) => {
    const userId = rootGetters[authentication.getters.firebaseUid];
    const userDisplayName = rootGetters[authentication.getters.displayName];
    const analysisId = await getAccessibleAnalysisIdForDocument(
      document,
      userId,
      userDisplayName,
      'User',
      userId,
      userDisplayName,
    );
    const idPath = ids.join(CATEGORIES_ID_SEPARATOR);
    try {
      await addElementToArray('reflections', reflectionId, `categories.${idPath}.analyses`, analysisId);
    } catch (e) {
      console.error('FAILURE: reflections.actions.addReflectionCategoryAnalysis', e);
      bugsnagClient.notify(e);
    }
  },
  addReportComment: async ({ dispatch, state }, { categoryId, comment, reflectionId, reportId }) => {
    const { report } = state;
    const id = getNewDocumentId();
    dispatch(ux.actions.disableAddComment, true, { root: true });
    dispatch(ux.actions.disableAddReply, true, { root: true });

    const comments = { ...report.comments };
    const list = comments[categoryId] || [];
    list.push({ ...comment, id, createdBy: getCurrentUserUid(), createdOn: now(), replies: [] });

    try {
      await updateReflectionReport(reflectionId, reportId, { comments: { ...report.comments, [categoryId]: list } });
    } catch (error) {
      bugsnagClient.notify(error);
      console.error('FAILURE: reflections.addReportComment', error);
    }

    dispatch(ux.actions.disableAddComment, false, { root: true });
    dispatch(ux.actions.disableAddReply, false, { root: true });
  },
  addReportCommentReaction: async ({ state }, { categoryId, commentId, reflectionId, reaction, reportId }) => {
    const { report } = state;
    const comments = { ...report.comments };
    const list = [...comments[categoryId]];
    const i = list.findIndex(({ id }) => commentId === id);
    list[i].reactions = { ...list[i].reactions, [`reactions.${getCurrentUserUid()}`]: reaction };

    try {
      await updateReflectionReport(reflectionId, reportId, { comments: { ...report.comments, [categoryId]: list } });
    } catch (error) {
      bugsnagClient.notify(error);
      console.error('FAILURE: reflections.addReportCommentReaction', error);
    }
  },
  addReportCommentReply: async ({ dispatch, state }, { categoryId, commentId, reflectionId, reply, reportId }) => {
    const { report } = state;
    const id = getNewDocumentId();
    dispatch(ux.actions.disableAddComment, true, { root: true });
    dispatch(ux.actions.disableAddReply, true, { root: true });

    const nextReply = { ...reply, id, commentId, createdBy: getCurrentUserUid(), createdOn: now() };
    const comments = { ...report.comments };
    const list = [...comments[categoryId]];
    const i = list.findIndex((comment) => commentId === comment.id);
    list[i].replies = list[i].replies ? [...list[i].replies, nextReply] : [nextReply];

    try {
      await updateReflectionReport(reflectionId, reportId, { comments: { ...report.comments, [categoryId]: list } });
    } catch (error) {
      bugsnagClient.notify(error);
      console.error('FAILURE: reflections.addReportCommentReply', error);
    }

    dispatch(ux.actions.disableAddComment, false, { root: true });
    dispatch(ux.actions.disableAddReply, false, { root: true });
  },
  [ACTION_CREATE_REFLECTION]: async ({ rootGetters, commit, dispatch }, reflection) => {
    commit(MUTATION_SET_CREATE_IN_PROGRESS, true);
    const userDisplayName = rootGetters[authentication.getters.displayName];
    const currentUserId = getCurrentUserUid();
    let reflectionId = reflection.id || await getNewDocumentId();

    try {
      await saveNewReflection(reflectionId, {
        ...reflection,
        id: reflectionId,
        createdBy: currentUserId,
        createdOn: now(),
        template: reflection.template.name,
        templateApplied: false,
        acl: [...reflection.acl, buildAclObject('User', currentUserId, userDisplayName)],
        status: 'IN_PROGRESS',
        type: 'CONVERSATION',
        categories: createDefaultReflectionCategories(),
        reflectionDocumentIds: [],
      });

      commit(MUTATION_SET_CURRENT_REFLECTION_ID, reflectionId);
      dispatch('handleOperationCompletion', { message: 'components.Reflection.success', color: 'success' }, { root: true });
    } catch (error) {
      dispatch('handleOperationCompletion', { message: 'errors.generic', color: 'error' }, { root: true });
      bugsnagClient.notify(error);
      console.error('FAILURE: ACTION_CREATE_REFLECTION', error);
      reflectionId = null;
    }

    commit(MUTATION_SET_CREATE_IN_PROGRESS, false);
    return reflectionId;
  },
  [ACTION_MARK_TEMPLATE_APPLIED]: async ({ getters: localGetters }) => {
    try {
      await editReflection(localGetters[GETTER_REFLECTION_ID], { templateApplied: true });
    } catch (e) {
      console.error('FAILURE: ACTION_TEMPLATE_APPLIED', e);
      bugsnagClient.notify(e);
    }
  },
  updateReportReflection: async ({ commit }, { reflectionId, reportId, report }) => {
    if (!report.lockedOn) {
      commit(MUTATION_SET_CURRENT_REPORT_ID, reportId);
      const id = reportId || getNewDocumentId();
      const edit = {
        ...report,
        id,
        createdBy: report.createdBy || getCurrentUserUid(),
        createdOn: report.createdOn || now(),
      };

      try {
        await updateReflectionReport(reflectionId, id, edit);
        commit(MUTATION_FETCHED_CURRENT_REPORT, edit);
        return id;
      } catch (error) {
        bugsnagClient.notify(error);
        console.error('FAILURE: reflections.updateReportReflection', error);
      }
    }

    return null;
  },
  [ACTION_FETCH_REFLECTION]: async ({ commit, dispatch, state }, { reflectionId, retryCount = 0 }) => {
    commit(MUTATION_SET_FETCH_IN_PROGRESS, true);
    commit(MUTATION_SET_CURRENT_REFLECTION_ID, reflectionId);

    try {
      await pingReflection(reflectionId);
    } catch (e) {
      if (retryCount < 10) {
        await delay((retryCount ** 2) * 1000);
        dispatch(ACTION_FETCH_REFLECTION, { reflectionId, retryCount: (retryCount + 1) });
      }
      return null;
    }

    try {
      const reflectionSnapshot = await onSnapshotById(
        'reflections', reflectionId, debounce(onReflectionSnapshot.bind(this, { commit }), SNAPSHOT_DEBOUNCE),
      );

      const analysesSnapshot = onSnapshotByQueryOnSubCollection(
        reflectionId, 'reflections', 'reflectionDocuments', [],
        debounce(onAnalysisSnapshots.bind(this, { commit, state }), SNAPSHOT_DEBOUNCE),
      );

      const reportsSnapshot = onSnapshotByQueryOnSubCollection(
        reflectionId, 'reflections', 'reflectionReports', [],
        debounce(onReportSnapshots.bind(this, { commit, dispatch, reflectionId }), SNAPSHOT_DEBOUNCE),
      );

      const userId = getCurrentUserUid();
      const denormAclSnapshot = onSnapshotById(
        `reflections/${reflectionId}/reflectionAcl`,
        userId,
        onReflectionAclDenormSnapshot.bind(this, { commit, dispatch, reflectionId }),
      );

      commit(
        MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: analysesSnapshot, id: SNAPSHOT_IDS.DOCUMENTS }, { root: true },
      );
      commit(
        MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: reportsSnapshot, id: SNAPSHOT_IDS.REPORTS }, { root: true },
      );
      commit(
        MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: reflectionSnapshot, id: SNAPSHOT_IDS.REFLECTION },
        { root: true },
      );
      commit(
        MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: denormAclSnapshot, id: SNAPSHOT_IDS.REFLECTION_PERMISSION },
        { root: true },
      );
    } catch (error) {
      bugsnagClient.notify(error);
      console.error('FAILURE: ACTION_FETCH_REFLECTION', error);
      commit(MUTATION_SET_FETCH_IN_PROGRESS, false);
    }

    return null;
  },
  // TODO JG refactor this action to take reflectionComment (thread) id and load only one of them
  // also prepare its internal data required for rendering right now, such as the user displayName
  // [ACTION_FETCH_CUR_REFLECTION_COMMENTS]: async ({ commit }, reflectionId) => {
  //
  [ACTION_GET_REFLECTION_COMMENT_THREAD]: ({ commit }, { reflectionId, threadId, callback }) => {
    const snapshotListener = onSnapshotById(
      `reflections/${reflectionId}/reflectionComments`,
      threadId,
      (threadRef) => { callback(threadRef.data()); },
    );


    // TODO Unregister this properly in component destroy
    commit(MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener, id: `${SNAPSHOT_IDS.COMMENTS}.${threadId}` }, { root: true });
  },
  [ACTION_GET_CURRENT_REPORT_BY_ID]: ({ commit, state }, reportId) => {
    const { reports = [] } = state;
    commit(MUTATION_SET_CURRENT_REPORT_ID, reportId);

    if (reports && reports.length) {
      if (reportId) {
        commit(MUTATION_FETCHED_CURRENT_REPORT, reports.find(({ id }) => id === reportId));
      } else {
        const report = reports.find(({ lockedOn }) => !lockedOn) || {};
        commit(MUTATION_FETCHED_CURRENT_REPORT, report);
      }
    }
  },
  lockReportReflection: async ({ commit, state }, { analyses, reflectionId, report }) => {
    const { reports } = state;
    const nextId = await getNewDocumentId();
    const nextReport = { ...report, id: nextId };

    try {
      await updateReflectionReport(reflectionId, report.id, { analyses, lockedOn: now() });
      await setReflectionReport(reflectionId, nextId, nextReport);
      commit(MUTATION_SET_CURRENT_REPORT_ID, nextId);
      commit(MUTATION_FETCHED_REPORTS, [...reports, nextReport]);
    } catch (error) {
      bugsnagClient.notify(error);
      console.error('FAILURE: ACTION_LOCK_REPORT_REFLECTION', error);
    }

    return null;
  },
  [ACTION_SET_ANALYSIS_REFLECTION_RETENTION]: async (_, { categoryId, documentId, reflectionId, retention }) => {
    try {
      await updateReflectionDocument(
        documentId, reflectionId, { [`retentionStatus.${categoryId}`]: retention },
      );
    } catch (error) {
      bugsnagClient.notify(error);
      console.error('FAILURE: ACTION_SET_ANALYSIS_REFLECTION_RETENTION', error);
    }

    return null;
  },
  unsubscribeReflection: ({ commit }) => {
    commit(MUTATION_UNREGISTER_SNAPSHOT_LISTENER, { id: SNAPSHOT_IDS.COMMENTS }, { root: true });
    commit(MUTATION_UNREGISTER_SNAPSHOT_LISTENER, { id: SNAPSHOT_IDS.DOCUMENTS }, { root: true });
    commit(MUTATION_UNREGISTER_SNAPSHOT_LISTENER, { id: SNAPSHOT_IDS.REFLECTION }, { root: true });
    commit(MUTATION_UNREGISTER_SNAPSHOT_LISTENER, { id: SNAPSHOT_IDS.REPORTS }, { root: true });

    commit('mutateReflectionCategoryDraft', null);
    commit(MUTATION_SET_CURRENT_REFLECTION_ID, null);
    commit(MUTATION_FETCHED_REFLECTION, null);
    commit(MUTATION_FETCHED_REFLECTION_COMMENTS, null);
    commit(MUTATION_FETCHED_CURRENT_REPORT, null);
  },
  unsubscribeReflections: ({ commit }) => {
    commit(MUTATION_UNREGISTER_SNAPSHOT_LISTENER, { id: SNAPSHOT_IDS.REFLECTIONS }, { root: true });
  },
  unsubscribeFromReflectionChanges: ({ commit }) => {
    commit(MUTATION_UNREGISTER_SNAPSHOT_LISTENER, { id: SNAPSHOT_IDS.REFLECTION_ONCHANGE }, { root: true });
  },
  removeReflectionAnalysis: async (_, { reflectionId, document }) => {
    try {
      await removeElementToArray('reflections', reflectionId, 'reflectionDocumentIds', document.id);
      await removeReflectionDocument(document.id, reflectionId);
    } catch (e) {
      console.error('FAILURE: reflections.actions.removeReflectionAnalysis', e);
      bugsnagClient.notify(e);
    }
  },
  removeReflectionCategoryAnalysis: async (_, { reflectionId, document, ids }) => {
    const idPath = ids.join(CATEGORIES_ID_SEPARATOR);
    try {
      await removeElementToArray('reflections', reflectionId, `categories.${idPath}.analyses`, document.id);
    } catch (e) {
      console.error('FAILURE: reflections.actions.removeReflectionCategoryAnalysis', e);
      bugsnagClient.notify(e);
    }
  },
  resetReflectionCategoryDraft: ({ commit, state }, reflectionId) => {
    const { reflections } = state;
    const reflection = reflections.find(({ id }) => (id === reflectionId));
    commit('mutateReflectionCategoryDraft', cloneDeep(reflection.categories));
  },
  sendReflectionCategoryDraft: async ({ commit, state }, { reflectionId }) => {
    const { reflectionCategoryDraft } = state;
    try {
      await editReflection(reflectionId, { categories: reflectionCategoryDraft });
      commit('mutateReflectionCategoryDraft', null);
    } catch (e) {
      console.error('FAILURE: reflections.actions.sendReflectionCategoryDraft', e);
      bugsnagClient.notify(e);
    }
  },
  updateCurrentReflection: async ({ commit, state }, { categories }) => {
    const { reflection } = state;
    const { id } = reflection || {};

    const edit = {};
    if (categories) {
      edit.categories = cloneDeep(categories);
    }

    if (Object.keys(edit).length) {
      try {
        await editReflection(id, edit);
        commit(MUTATION_FETCHED_REFLECTION, { ...reflection, ...edit });
      } catch (error) {
        bugsnagClient.notify(error);
        console.error('FAILURE: updateCurrentReflection', error);
        throw error;
      }
    }

    return null;
  },
  [ACTION_UPDATE_CURRENT_REFLECTION_DATA_STATE]: async ({ commit }, blocks) => {
    // Updates state only, don't touch firestore
    await commit(MUTATION_SET_CURRENT_REFLECTION_DATA, { data: blocks });
  },
  [ACTION_UPDATE_REFLECTION]: async (_, { reflectionId, updateData }) => {
    console.log('UPDATING REFLECTION', reflectionId, updateData);
    try {
      await update('reflections', reflectionId, updateData);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  },
  [ACTION_UPDATE_CURRENT_REFLECTION_DATA]: async ({ state }, editorData) => {
    const { reflection } = state;
    const { id } = reflection || {};


    if (Object.keys(id).length === 0 && id.constructor === Object) {
      // No reflection id, should throw an error
      console.log('Error: no reflection id!!!');
      // throw...?
    }

    const editorContent = {
      editorData,
    };

    let updateResult = null;
    try {
      updateResult = await update('reflections', id, editorContent);
    } catch (e) {
      console.log('Error saving reflection data', e);
      throw e;
    }
    return updateResult;
  },
  [ACTION_CREATE_CUR_REFLECTION_COMMENT]: async ({ rootGetters }, { reflectionId, threadId, commentData }) => {
    const newCommentData = {
      ...commentData,
      createdBy: getCurrentUserUid(),
      createdByDisplayName: rootGetters[authentication.getters.displayName],
      createdOn: now(),
    };
    try {
      await setMerge(
        `reflections/${reflectionId}/reflectionComments`,
        threadId,
        { id: threadId, commentsThread: getFieldValue().arrayUnion(newCommentData) },
        { undefinedSafe: true },
      );
      console.log('Reflection comments updated!');
    } catch (e) {
      console.log('Error saving reflection comments data', e);
      throw e;
    }
  },
  [ACTION_ARCHIVE_REFLECTION]: async ({ commit }, { reflectionId, isArchived }) => {
    console.log('ARCHIVING REFLECTION', reflectionId);
    await update('reflections', reflectionId, { isArchived });
    commit(MUTATION_ARCHIVE_REFLECTIONS, { isArchived, reflectionId });
  },
  [ACTION_SUBSCRIBE_TO_REFLECTION_CHANGES]: async ({ commit, state }, reflectionId) => {
    console.log('%c--> into [ACTION_SUBSCRIBE_TO_REFLECTION_CHANGES]', 'color:blue;');

    // TODO don't forget to unsubscribe at some point
    const unsubscribeReflectionRedactionChange = onSnapshotByQuery(
      `reflections/${reflectionId}/changes`,
      onCurrentReflectionRedactionChange.bind(this, { commit, state }, reflectionId),
      [],
      { field: 'data.data.timestamp', order: 'desc' },
      1,
    );

    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER,
      { snapshotListener: unsubscribeReflectionRedactionChange, id: SNAPSHOT_IDS.REFLECTION_ONCHANGE },
      { root: true },
    );
  },
  [ACTION_UPDATE_FETCHED_REFLECTIONS]: async ({ commit }) => {
    const currentUserId = getCurrentUserUid();
    const reflectionsData = await findRawGroup('reflectionAcl', [{ key: 'id', operator: '==', value: currentUserId }]);
    await onReflectionAclSnapshots({ commit }, reflectionsData);
  },
  [ACTION_VALIDATE_SUPPORTING_DOCS_ACL]: async ({ rootGetters, state }, { acl }) => {
    const organizationId = rootGetters[authentication.getters.organizationId];
    const reflectionId = state.reflection.id;
    const analyses = state.currentReflectionAnalyses || [];
    const analysisIds = analyses.map(a => a.id);

    const validator = new ReflectionAccessValidator(reflectionId, organizationId, analysisIds);
    await validator.validate(acl);
  },
};

export async function onCurrentReflectionRedactionChange({ commit, state }, _, snapshot) {
  console.log('%c--> into SNAPSHOT HOOK [onCurrentReflectionRedactionChange]', 'color:blue;');

  const snapshotData = snapshot.docs[0].data().data;
  const lastChangeSignature = snapshotData.data.blocksSignature;
  const stateSignature = state.currentReflectionData.blocksSignature;

  /*
  // TODO not sure about this one, timestamp is currently set from the client, pas fiable...
  if (snapshotData.data.timestamp <= state.currentReflectionData.timestamp) {
    console.error('Snapshot is OLDER or EQUAL to state, ignoring');
    return
  }
  */

  if (stateSignature) { // NOT defined at first invocation when binding
    if (stateSignature === lastChangeSignature) {
      console.log('%cChange signature matches state signature, nothing to update', 'color:green;');
    } else {
      console.log('%cChange signature is different from state signature, update required', 'color:magenta;');
      await commit(MUTATION_SET_CURRENT_REFLECTION_DATA, snapshotData);
    }
  }
}

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};

export const EReflectionAnalysisRetention = {
  considered: 'considered',
  notRetained: 'notRetained',
  retained: 'retained',
};

export const types = {
  getters: {
    addAnalysisToReflectionInProgress: `${MODULE_PREFIX}/${GETTER_ADD_ANALYSIS_TO_REFLECTION_IN_PROGRESS}`,
    comments: `${MODULE_PREFIX}/${GETTER_CURRENT_REFLECTION_COMMENTS}`,
    curReflectionComments: `${MODULE_PREFIX}/${GETTER_CUR_REFLECTION_COMMENTS}`,
    createInProgress: `${MODULE_PREFIX}/${GETTER_CREATE_IN_PROGRESS}`,
    currentReflectionAnalyses: `${MODULE_PREFIX}/${GETTER_CURRENT_ANALYSES}`,
    fetchCurrentReflectionData: `${MODULE_PREFIX}/${GETTER_FETCH_CURRENT_REFLECTION_DATA}`,
    fetchPreviousReflectionData: `${MODULE_PREFIX}/${GETTER_FETCH_PREVIOUS_REFLECTION_DATA}`,
    fetchInProgress: `${MODULE_PREFIX}/${GETTER_FETCH_IN_PROGRESS}`,
    loadingComments: `${MODULE_PREFIX}/${GETTER_LOADING_COMMENTS}`,
    reflection: `${MODULE_PREFIX}/${GETTER_REFLECTION}`,
    reflectionId: `${MODULE_PREFIX}/${GETTER_REFLECTION_ID}`,
    reflectionReadOnly: `${MODULE_PREFIX}/${GETTER_REFLECTION_READONLY}`,
    reflectionCategoryDraft: `${MODULE_PREFIX}/reflectionCategoryDraft`,
    reflections: `${MODULE_PREFIX}/${GETTER_REFLECTIONS}`,
    report: `${MODULE_PREFIX}/${GETTER_CURRENT_REPORT}`,
    reports: `${MODULE_PREFIX}/${GETTER_CURRENT_REPORTS}`,
  },
  mutations: {
    mutateReflectionCategoryDraft: `${MODULE_PREFIX}/mutateReflectionCategoryDraft`,
    setCurrentReflectionData: `${MODULE_PREFIX}/${MUTATION_SET_CURRENT_REFLECTION_DATA}`,
    setPreviousReflectionData: `${MODULE_PREFIX}/${MUTATION_SET_PREVIOUS_REFLECTION_DATA}`,
  },
  actions: {
    addReflectionAnalysis: `${MODULE_PREFIX}/addReflectionAnalysis`,
    addReflectionCategoryAnalysis: `${MODULE_PREFIX}/addReflectionCategoryAnalysis`,
    addReportComment: `${MODULE_PREFIX}/addReportComment`,
    addReportCommentReaction: `${MODULE_PREFIX}/addReportCommentReaction`,
    addReportCommentReply: `${MODULE_PREFIX}/addReportCommentReply`,
    createReflection: `${MODULE_PREFIX}/${ACTION_CREATE_REFLECTION}`,
    createCurReflectionComment: `${MODULE_PREFIX}/${ACTION_CREATE_CUR_REFLECTION_COMMENT}`,
    fetchReflection: `${MODULE_PREFIX}/${ACTION_FETCH_REFLECTION}`,
    markTemplateApplied: `${MODULE_PREFIX}/${ACTION_MARK_TEMPLATE_APPLIED}`,
    getReflectionCommentThread: `${MODULE_PREFIX}/${ACTION_GET_REFLECTION_COMMENT_THREAD}`,
    getCurrentReportById: `${MODULE_PREFIX}/${ACTION_GET_CURRENT_REPORT_BY_ID}`,
    lockReportReflection: `${MODULE_PREFIX}/lockReportReflection`,
    removeReflectionAnalysis: `${MODULE_PREFIX}/removeReflectionAnalysis`,
    removeReflectionCategoryAnalysis: `${MODULE_PREFIX}/removeReflectionCategoryAnalysis`,
    resetReflectionCategoryDraft: `${MODULE_PREFIX}/resetReflectionCategoryDraft`,
    sendReflectionCategoryDraft: `${MODULE_PREFIX}/sendReflectionCategoryDraft`,
    setAnalysisReflectionRetention: `${MODULE_PREFIX}/${ACTION_SET_ANALYSIS_REFLECTION_RETENTION}`,
    subscribeToReflectionChanges: `${MODULE_PREFIX}/${ACTION_SUBSCRIBE_TO_REFLECTION_CHANGES}`,
    unsubscribeReflection: `${MODULE_PREFIX}/unsubscribeReflection`,
    unsubscribeReflections: `${MODULE_PREFIX}/unsubscribeReflections`,
    unsubscribeFromReflectionChanges: `${MODULE_PREFIX}/unsubscribeFromReflectionChanges`,
    updateCurrentReflection: `${MODULE_PREFIX}/updateCurrentReflection`,
    updateCurrentReflectionData: `${MODULE_PREFIX}/${ACTION_UPDATE_CURRENT_REFLECTION_DATA}`,
    updateCurrentReflectionDataState: `${MODULE_PREFIX}/${ACTION_UPDATE_CURRENT_REFLECTION_DATA_STATE}`,
    updateReportReflection: `${MODULE_PREFIX}/updateReportReflection`,
    updateReflection: `${MODULE_PREFIX}/updateReflection`,
    updateFetchedReflections: `${MODULE_PREFIX}/${ACTION_UPDATE_FETCHED_REFLECTIONS}`,
    archiveReflection: `${MODULE_PREFIX}/archiveReflection`,
    headerHasJournalSection: `${MODULE_PREFIX}/${ACTION_HEADER_HAS_JOURNAL_SECTION}`,
    validateSupportingDocsACL: `${MODULE_PREFIX}/${ACTION_VALIDATE_SUPPORTING_DOCS_ACL}`,
  },
};
